import { forwardRef } from 'react';
import { Link as RouterLink } from 'react-router-dom';
// @mui
import { Box, Link, ListItemText, Tooltip } from '@mui/material';
// locales
import { useLocales } from '../../../locales';
// auth
import RoleBasedGuard from '../../../auth/RoleBasedGuard';
//
import { useTheme } from '@mui/material';
import { keyframes } from '@mui/system';
import Iconify from '../../iconify';
import Label from '../../label';
import { NavItemProps } from '../types';
import { StyledIcon, StyledItem } from './styles';

// ----------------------------------------------------------------------

const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.06); }
  100% { transform: scale(1); }
`;

const NavItem = forwardRef<HTMLDivElement, NavItemProps>(
  ({ item, depth, open, active, isExternalLink, ...other }, ref) => {
    const theme = useTheme();
    const { translate } = useLocales();

    const {
      title,
      path,
      icon,
      info,
      children,
      disabled,
      caption,
      roles,
      label,
    } = item;

    const subItem = depth !== 1;

    const renderContent = (
      <StyledItem
        ref={ref}
        open={open}
        depth={depth}
        active={active}
        disabled={disabled}
        {...other}
      >
        {icon && <StyledIcon>{icon}</StyledIcon>}

        <ListItemText
          primary={`${translate(title)}`}
          primaryTypographyProps={{
            noWrap: !subItem,
            component: 'span',
            variant: active ? 'subtitle2' : 'body2',
          }}
        />

        {label && (
          <Label
            color={label.color}
            sx={{
              marginLeft: theme.spacing(1),
              backgroundColor: theme.palette.error.main,
              color: theme.palette.error.contrastText,
              willChange: 'transform',
              transformOrigin: 'center',
              ...(label.animated && {
                animation: `${pulse} 1.8s ease-in-out infinite`,
                '@media (prefers-reduced-motion: reduce)': {
                  animation: 'none',
                },
              }),
            }}
          >
            {translate(label.text)}
          </Label>
        )}

        {info && (
          <Box component="span" sx={{ ml: 1, lineHeight: 0 }}>
            {info}
          </Box>
        )}

        {caption && (
          <Tooltip title={`${translate(caption)}`} arrow>
            <Box component="span" sx={{ ml: 0.5, lineHeight: 0 }}>
              <Iconify icon="eva:info-outline" width={16} />
            </Box>
          </Tooltip>
        )}

        {!!children && (
          <Iconify
            icon={subItem ? 'eva:chevron-right-fill' : 'eva:chevron-down-fill'}
            width={16}
            sx={{ ml: 0.5, flexShrink: 0 }}
          />
        )}
      </StyledItem>
    );

    const renderItem = () => {
      // Has children - don't navigate, just show dropdown
      if (children) {
        return renderContent;
      }

      // ExternalLink
      if (isExternalLink)
        return (
          <Link href={path} target="_blank" rel="noopener" underline="none">
            {renderContent}
          </Link>
        );

      // Default
      return (
        <Link component={RouterLink} to={path} underline="none">
          {renderContent}
        </Link>
      );
    };

    return <RoleBasedGuard roles={roles}> {renderItem()} </RoleBasedGuard>;
  },
);

export default NavItem;
